// ============================================================
// Intigo reference data sync.
// Pulls cities/districts/neighborhoods/pickup-addresses from the carrier
// and feeds them to the data layer via IDeliveryProviderRepository.
// ============================================================

import type { IntigoClient } from './client';

export interface IntigoCity { id: number; name: string; name_ar?: string }
export interface IntigoDistrict { id: number; name: string }
export interface IntigoNeighborhood { id: number; name: string }
export interface IntigoPickup {
  index: number;
  street_address: string;
  city_name: string;
  district_name: string;
  has_gps: boolean;
}

export interface IntigoCitiesResponse { success: boolean; cities: IntigoCity[] }
export interface IntigoDistrictsResponse { success: boolean; city_id: number; districts: IntigoDistrict[] }
export interface IntigoNeighborhoodsResponse { success: boolean; district_id: number; neighborhoods: IntigoNeighborhood[] }
export interface IntigoPickupsResponse { success: boolean; pickup_addresses: IntigoPickup[] }

// All paths are RELATIVE to the proxy base URL (`/api/delivery/intigo`),
// which forwards to https://api.intigo.net/api/v3/. So we omit the `v3/`
// prefix here — adding it would double-up to /api/v3/v3/... and 404.

/** Fetch the full city catalog. */
export async function fetchCities(client: IntigoClient): Promise<IntigoCity[]> {
  const res = await client.get<IntigoCitiesResponse>('regions/cities');
  return res.cities ?? [];
}

/** Fetch districts of one city. */
export async function fetchDistricts(client: IntigoClient, cityId: number): Promise<IntigoDistrict[]> {
  const res = await client.get<IntigoDistrictsResponse>(`regions/cities/${cityId}/districts`);
  return res.districts ?? [];
}

/** Fetch neighborhoods of one district. */
export async function fetchNeighborhoods(client: IntigoClient, districtId: number): Promise<IntigoNeighborhood[]> {
  const res = await client.get<IntigoNeighborhoodsResponse>(`regions/districts/${districtId}/neighborhoods`);
  return res.neighborhoods ?? [];
}

/** Fetch the partner's pickup addresses. */
export async function fetchPickups(client: IntigoClient): Promise<IntigoPickup[]> {
  const res = await client.get<IntigoPickupsResponse>('pickup-addresses/');
  return res.pickup_addresses ?? [];
}
